import React, { useState } from 'react'

import {
    postApplication,
    postApplicationCollateralsAssets,
} from 'api/application'
import getFinalDecision from 'api/application/getFinalDecision'
import { createPostCollateralsAssetsDTO } from 'api/application/postAutomobileApplicationCollateralsAssets'
import { isUnauthorizedState } from 'api/auth/unauthorizedInterceptor'
import SecondCheckpointModal from 'components/CheckpointModal/SecondCheckpointModal'
import VerifiedIcon from 'components/Icons/VerifiedIcon'
import RoadrunnerDealershipAgreementNotice from 'components/notices/RoadrunnerDealershipAgreementNotice'
import { FormValues } from 'containers/FormBuilder'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import { useLender } from 'contexts/LenderContextProvider'
import useLenderDisclosures from 'hooks/useLenderDisclosures'
import useUser from 'hooks/useUser'
import LoadingPage from 'pages/LoadingPage/LoadingPage'
import DisclosureType from 'state/enums/DisclosureType'
import joinTextValues from 'utils/joinTextValues'
import * as toast from 'utils/toast'

import DeveloperFormDataQuickFill from '../DeveloperFormDataQuickFill/DeveloperFormDataQuickFill'
import {
    DISCLOSURE_LOAD_ERROR_MESSAGE,
    hasRequiredDisclosures,
} from '../disclosureUtils'
import {
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
    otherFieldNames,
} from '../sharedInstructions'
import { swapApplicantsFunc } from '../utils'
import { automobileFormInstructions } from './automobileApplicationFields'
import AutomobileFormBuilder, {
    AutomobileFormBuilderProps,
} from './AutomobileFormBuilder'
import { Label, Title, Titlebar } from './AutomobileFormBuilder.styled'
import createPostApplicationDTO from './createPostApplicationDTO'

type AutomobileApplicationFormProps = {
    overrideDefaultInitialValues?: AutomobileFormBuilderProps['overrideDefaultInitialValues']
    isLoading?: boolean
    isCopy?: boolean
    partner?: string
    id?: number
    crossoverApplication?: boolean
    isReadOnly?: boolean
}

const submitApplication = async (
    values: FormValues,
    setShowSubmissionModal: (state: boolean) => void,
    isCopy?: boolean,
    parentId?: number,
) => {
    setShowSubmissionModal(true)

    try {
        const applicationDTO = createPostApplicationDTO(
            values,
            isCopy,
            parentId,
        )

        const { uuid, id } = await postApplication(applicationDTO)
        const collaterals = createPostCollateralsAssetsDTO(values.vehicle)

        await postApplicationCollateralsAssets(uuid, collaterals)

        await getFinalDecision(uuid)
        // Temporarily routing directly to WTD page after submission.
        // BLUR-184 will resolve this temporary workaround for unintended
        // routing behavior.
        window.location.hash = `/application/status/${id}`
    } catch {
        setShowSubmissionModal(false)

        if (!isUnauthorizedState()) {
            toast.error({
                message:
                    'There was a problem submitting your application. Please contact us if this issue continues.',
            })
        }
    }
}

export default function AutomobileApplicationForm(
    props: AutomobileApplicationFormProps,
) {
    const {
        overrideDefaultInitialValues,
        isLoading,
        partner,
        isCopy,
        id,
        crossoverApplication,
        isReadOnly,
    } = props

    const { user } = useUser()
    const featureFlags = useFeatureFlagContext()
    const { id: lenderId } = useLender()

    const { disclosures, loading, retryAttemptsExhausted } =
        useLenderDisclosures(lenderId)

    const userFirstAndLast =
        user && joinTextValues([user.firstName, user.lastName])

    const [showSubmissionModal, setShowSubmissionModal] = useState(false)

    const [developerOverrideData, setDeveloperOverrideData] =
        useState<FormValues>()

    const sectionMargin = crossoverApplication ? '0' : '0 85px'
    const sectionWidth = crossoverApplication ? '100%' : 'calc(100% - 170px)'

    if (isLoading) {
        return <LoadingPage />
    }

    const disclosuresLoaded = hasRequiredDisclosures(disclosures, [
        DisclosureType.AUTOMOBILE_APPLICATION_FORM_DISCLOSURE,
    ])

    const disclosureLoadFailed = !loading && retryAttemptsExhausted
    const canSubmit = !loading && disclosuresLoaded
    const akHiEnabled = featureFlags?.akHiStateEnabled ?? false

    const initialValuesOverride =
        developerOverrideData || overrideDefaultInitialValues

    return (
        <>
            <RoadrunnerDealershipAgreementNotice margin={sectionMargin} />
            {disclosureLoadFailed && (
                <p role="alert" data-testid="disclosure-load-error-message">
                    {DISCLOSURE_LOAD_ERROR_MESSAGE}
                </p>
            )}

            {!crossoverApplication && !isReadOnly && (
                <Titlebar>
                    <Title data-testid="newapplication-title">
                        {isCopy ? 'Copy into New' : 'New Application'}
                    </Title>
                    <Label data-testid="newapplication-label">
                        Instant decisions • No credit impact &nbsp;
                        <VerifiedIcon />
                    </Label>
                </Titlebar>
            )}

            <SecondCheckpointModal isOpen={showSubmissionModal} animated />
            <DeveloperFormDataQuickFill
                setDeveloperOverrideData={setDeveloperOverrideData}
            />
            <AutomobileFormBuilder
                onSubmit={(values) =>
                    submitApplication(
                        values,
                        setShowSubmissionModal,
                        isCopy,
                        id,
                    )
                }
                collateralType="Automobile"
                instructions={automobileFormInstructions(
                    isReadOnly ?? false,
                    akHiEnabled,
                )}
                isApplicationJointFunc={isApplicationJointFunc}
                swapApplicantsFunc={swapApplicantsFunc}
                overrideDefaultInitialValues={{
                    [otherFieldNames.financeManager]: userFirstAndLast,
                    ...initialValuesOverride,
                }}
                sectionMargin={sectionMargin}
                sectionWidth={sectionWidth}
                enableReinitialize
                isCoapplicantAddressSameFunc={isCoapplicantAddressSameFunc}
                isCopy={isCopy}
                partner={partner}
                canSubmit={canSubmit}
                disclosureLoadFailed={disclosureLoadFailed}
                isReadOnly={isReadOnly}
            />
        </>
    )
}

AutomobileApplicationForm.defaultProps = {
    overrideDefaultInitialValues: undefined,
    isCopy: false,
    isLoading: false,
    partner: '',
    id: 0,
    isReadOnly: false,
}

export const testExports = {
    submitApplication,
}
